import React, { useEffect } from 'react';
import { Box, Button } from '@mui/material';
import { toast } from '../../../Components/Toast';
import { useCadUpload } from '../../hooks/useCadUpload';
import CadFilePicker from './CadFilePicker';
import CadPreviewStep from './CadPreviewStep';
import {
  actionsRowSx,
  flowRootSx,
  primaryButtonSx,
  secondaryButtonSx,
} from './CadUploadFlow.styles';

/**
 * Alta de una lista a partir del plano del loteo (`.dxf`).
 * Orden: archivo → (subida automática) → preview → confirmación.
 * Sin empresa: el dueño es el JWT.
 *
 * @param {{
 *   onCancel: () => void,
 *   onCreated: (project: object) => void,
 *   onCadWorkChange?: (busy: boolean) => void,
 * }} props
 * @see docs/maps/cad-upload.md
 */
export default function CadUploadFlow({ onCancel, onCreated, onCadWorkChange }) {
  const upload = useCadUpload({
    onCreated: (project) => {
      toast.success('Lista creada con éxito.');
      onCreated?.(project);
    },
  });

  const {
    step,
    file,
    requirements,
    selectFile,
    clearFile,
    uploading,
    uploadStage,
    uploadAndParse,
    reparseWithLayer,
    preview,
    listName,
    setListName,
    creating,
    confirmCreate,
    discardPreview,
    error,
  } = upload;

  const hasCadWork = Boolean(file || preview || uploading);
  useEffect(() => {
    onCadWorkChange?.(hasCadWork);
    return () => onCadWorkChange?.(false);
  }, [hasCadWork, onCadWorkChange]);

  const handleSelectFile = (nextFile) => {
    if (!selectFile(nextFile)) return;
    void uploadAndParse({ file: nextFile });
  };

  if (step === 'preview' && preview) {
    return (
      <Box sx={flowRootSx}>
        <CadPreviewStep
          preview={preview}
          name={listName}
          onNameChange={setListName}
          creating={creating}
          reparsing={uploading}
          error={error}
          onConfirm={confirmCreate}
          onDiscard={discardPreview}
          onRetryLayer={reparseWithLayer}
        />
      </Box>
    );
  }

  return (
    <Box sx={flowRootSx}>
      <CadFilePicker
        file={file}
        requirements={requirements}
        error={error}
        disabled={uploading}
        busy={uploading}
        busyStage={uploadStage}
        onSelect={handleSelectFile}
        onClear={clearFile}
      />

      <Box sx={actionsRowSx}>
        <Button variant="outlined" onClick={onCancel} disabled={uploading} sx={secondaryButtonSx}>
          Cancelar
        </Button>
        {file && error && !uploading ? (
          <Button
            variant="contained"
            onClick={() => uploadAndParse()}
            sx={primaryButtonSx}
          >
            Reintentar
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}
